import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';

import { useGetUserAllLeaveBalanceQuery } from '../../../../redux/apis/LeaveBalance';
import { useApplyViolationLeaveMutation } from '../../../../redux/apis/attendanceViolationApi';

import {
  buildViolationApplyLeavePayload,
  filterViolationApplyLeaveTypes,
  formatViolationDateLong,
  formatViolationLeaveDeductionLabel,
  getLeaveTypeName,
  getViolationDateNum,
  getViolationEmployeeName,
  getViolationId,
  getViolationLeaveTypeBalance,
  getViolationLeaveTypeDisplayName,
  getViolationTypeLabel,
  getViolationUserId,
  isUnpaidLeaveType,
  isViolationLeaveTypeSelectable
} from '../../../../utils/attendanceViolationHelpers';
import { notification } from '../../../../utils/notification';
import { TOAST_TYPE } from '../../../../utils/constant/errorMessage';

const { TOAST_SUCCESS, TOAST_ERROR } = TOAST_TYPE;

const LeaveTypePicker = ({ leaveTypes, selectedLeaveTypeId, onSelect, disabled }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const selectedLeaveType = leaveTypes.find((row) => row?._id === selectedLeaveTypeId) || null;

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  return (
    <div
      ref={wrapRef}
      className={`att-violation-leave-picker${open ? ' att-violation-leave-picker--open' : ''}`}
    >
      <button
        type="button"
        id="att-violation-leave-type"
        className="att-violation-leave-picker__trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span
          className={`att-violation-leave-picker__value${
            !selectedLeaveType ? ' att-violation-leave-picker__value--placeholder' : ''
          }`}
        >
          {selectedLeaveType
            ? getViolationLeaveTypeDisplayName(selectedLeaveType)
            : 'Choose leave type'}
        </span>
        <i className="bi bi-chevron-down att-violation-leave-picker__chevron" aria-hidden />
      </button>

      {open ? (
        <ul className="att-violation-leave-picker__menu" role="listbox">
          {leaveTypes.map((row) => {
            const balance = getViolationLeaveTypeBalance(row);
            const unpaid = isUnpaidLeaveType(row);
            const selectable = isViolationLeaveTypeSelectable(row);
            const isSelected = selectedLeaveTypeId === row?._id;

            return (
              <li key={row?._id} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`att-violation-leave-picker__option${
                    isSelected ? ' att-violation-leave-picker__option--selected' : ''
                  }${!selectable ? ' att-violation-leave-picker__option--disabled' : ''}`}
                  disabled={!selectable}
                  onClick={() => {
                    onSelect(row._id);
                    setOpen(false);
                  }}
                >
                  <span className="att-violation-leave-picker__option-name">{row?.name}</span>
                  {!unpaid ? (
                    <span
                      className={`att-violation-leave-picker__option-balance${
                        balance <= 0 ? ' att-violation-leave-picker__option-balance--empty' : ''
                      }`}
                    >
                      {balance} day{balance === 1 ? '' : 's'}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

const ApplyLeaveModal = ({ show, violation, onClose, onSuccess }) => {
  const userId = getViolationUserId(violation);
  const suggestedLeaveTypeName = getLeaveTypeName(violation?.violationType);
  const [selectedLeaveTypeId, setSelectedLeaveTypeId] = useState('');

  const { data: balanceResponse, isFetching: balanceLoading } = useGetUserAllLeaveBalanceQuery(
    { userId },
    { skip: !show || !userId }
  );

  const [applyViolationLeave, { isLoading: submitting }] = useApplyViolationLeaveMutation();

  const leaveTypes = useMemo(() => {
    const rows = balanceResponse?.data?.[0]?.leaveType;
    const all = Array.isArray(rows) ? rows : [];
    return filterViolationApplyLeaveTypes(all, violation?.violationType);
  }, [balanceResponse, violation?.violationType]);

  const selectedLeaveType = useMemo(
    () => leaveTypes.find((row) => row?._id === selectedLeaveTypeId) || null,
    [leaveTypes, selectedLeaveTypeId]
  );

  useEffect(() => {
    if (!show) {
      setSelectedLeaveTypeId('');
      return;
    }

    const suggested = leaveTypes.find((row) => {
      const name = String(row?.name || '')
        .trim()
        .toLowerCase();
      return (
        name === suggestedLeaveTypeName.trim().toLowerCase() && isViolationLeaveTypeSelectable(row)
      );
    });

    const firstSelectable = leaveTypes.find(isViolationLeaveTypeSelectable);

    setSelectedLeaveTypeId(suggested?._id || firstSelectable?._id || '');
  }, [show, leaveTypes, suggestedLeaveTypeName]);

  const showBalanceForSelected = selectedLeaveType && !isUnpaidLeaveType(selectedLeaveType);
  const selectedBalance = selectedLeaveType
    ? getViolationLeaveTypeBalance(selectedLeaveType)
    : null;
  const deductionLabel = selectedLeaveType
    ? formatViolationLeaveDeductionLabel(selectedLeaveType)
    : null;
  const canApplySelected =
    Boolean(selectedLeaveTypeId) && isViolationLeaveTypeSelectable(selectedLeaveType);

  const handleApply = async () => {
    const violationId = getViolationId(violation);

    if (!violationId) {
      notification('Violation id is missing.', TOAST_ERROR);
      return;
    }

    if (!selectedLeaveTypeId) {
      notification('Please select a leave type.', TOAST_ERROR);
      return;
    }

    if (!canApplySelected) {
      notification('Selected leave type does not have enough balance.', TOAST_ERROR);
      return;
    }

    try {
      const payload = buildViolationApplyLeavePayload(violation, selectedLeaveTypeId);
      const res = await applyViolationLeave(payload);

      if (res?.error) {
        notification(res?.error?.data?.statusMessage || 'Failed to apply leave.', TOAST_ERROR);
        return;
      }

      notification(
        res?.data?.statusMessage || 'Leave approved and applied successfully.',
        TOAST_SUCCESS
      );
      onSuccess?.();
      onClose?.();
    } catch (error) {
      notification(error?.message || 'Failed to apply leave.', TOAST_ERROR);
    }
  };

  return (
    <Modal centered show={show} onHide={onClose} className="att-violation-apply-modal">
      <Modal.Header closeButton className="att-violation-apply-modal__head">
        <Modal.Title>Apply Leave</Modal.Title>
      </Modal.Header>
      <Modal.Body className="att-violation-apply-modal__body">
        <div className="att-violation-modal">
          <div className="att-violation-modal__summary">
            <div className="att-violation-modal__summary-item">
              <span className="att-violation-modal__label">Employee</span>
              <strong>{getViolationEmployeeName(violation)}</strong>
            </div>
            <div className="att-violation-modal__summary-item">
              <span className="att-violation-modal__label">Violation date</span>
              <strong>{formatViolationDateLong(getViolationDateNum(violation))}</strong>
            </div>
            <div className="att-violation-modal__summary-item">
              <span className="att-violation-modal__label">Violation type</span>
              <strong className="att-violation-modal__type">
                {getViolationTypeLabel(violation?.violationType)}
              </strong>
            </div>
          </div>

          <div className="att-violation-modal__field">
            <label className="att-violation-modal__field-label" htmlFor="att-violation-leave-type">
              Select leave type
            </label>

            {balanceLoading ? (
              <div className="att-violation-leave-picker att-violation-leave-picker--loading">
                <Spinner animation="border" size="sm" role="status" />
                <span>Loading leave balances…</span>
              </div>
            ) : !leaveTypes.length ? (
              <p className="att-violation-leave-picker__empty">No leave types available.</p>
            ) : (
              <LeaveTypePicker
                leaveTypes={leaveTypes}
                selectedLeaveTypeId={selectedLeaveTypeId}
                onSelect={setSelectedLeaveTypeId}
                disabled={balanceLoading}
              />
            )}
          </div>

          {selectedLeaveType ? (
            <div className="att-violation-modal__meta-strip">
              <div className="att-violation-modal__meta-item att-violation-modal__meta-item--deduct">
                <span>Leave deduction</span>
                <strong>{deductionLabel}</strong>
              </div>
              {showBalanceForSelected ? (
                <div className="att-violation-modal__meta-item">
                  <span>Available balance</span>
                  <strong>
                    {selectedBalance} day{selectedBalance === 1 ? '' : 's'}
                  </strong>
                </div>
              ) : null}
            </div>
          ) : null}

          <p className="att-violation-modal__note">
            An approved leave of <strong>{deductionLabel || 'the selected amount'}</strong> will be
            applied for this violation date. Paid / Unpaid leave deducts <strong>0.5 day</strong>;
            Late Arrival / Early Exit deducts <strong>1 day</strong>.
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer className="att-violation-apply-modal__foot">
        <Button variant="light" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleApply}
          disabled={submitting || balanceLoading || !canApplySelected}
        >
          {submitting ? 'Applying…' : 'Apply Approved Leave'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ApplyLeaveModal;
